import React, { useEffect, useMemo, useRef, useState } from "react";
import theme from "../../../styles/theme";
import drawSpaceConfig from "../config/drawSpaceConfig";
import { buildGridLines } from "../utils/drawGrid";

/*
====================================================================
UTILITAIRES LOCAUX
====================================================================
*/

const snapPoint = (point, gridSize, snapToGrid) => {
  if (!snapToGrid) return point;

  return {
    x: Math.round(point.x / gridSize) * gridSize,
    y: Math.round(point.y / gridSize) * gridSize,
  };
};

const getCursor = (tool, isPanning) => {
  if (isPanning) return "grabbing";
  if (tool === drawSpaceConfig.tools.PAN) return "grab";

  if (
    tool === drawSpaceConfig.tools.AREA_RECT ||
    tool === drawSpaceConfig.tools.AREA_POLYLINE ||
    tool === drawSpaceConfig.tools.LINE ||
    tool === drawSpaceConfig.tools.CIRCLE
  ) {
    return "crosshair";
  }

  if (tool === drawSpaceConfig.tools.MOVE) return "move";

  return "default";
};

const isCreationTool = (tool) => {
  return (
    tool === drawSpaceConfig.tools.AREA_RECT ||
    tool === drawSpaceConfig.tools.AREA_POLYLINE ||
    tool === drawSpaceConfig.tools.LINE ||
    tool === drawSpaceConfig.tools.CIRCLE
  );
};

const getModeLabel = (activeMode) => {
  if (activeMode === "SITE") return "Mode : Site";
  if (activeMode === "OUVRAGE") return "Mode : Ouvrage";
  if (activeMode === "NIVEAU") return "Mode : Level";
  return "Mode : -";
};

const getActivePalette = (activeMode) => {
  if (activeMode === "SITE") return drawSpaceConfig.palettes.ouvrage;
  if (activeMode === "OUVRAGE") return drawSpaceConfig.palettes.level;
  return drawSpaceConfig.palettes.area;
};

const getPaletteByKey = (paletteKey, fallbackPalette) => {
  if (!paletteKey) return fallbackPalette;
  return drawSpaceConfig.palettes[paletteKey] ?? fallbackPalette;
};

/*
====================================================================
COMPOSANT PRINCIPAL
====================================================================
*/

const DrawViewport = ({
  width,
  height,

  entities = [],
  selectedId,

  zoom,
  offsetX,
  offsetY,

  showGrid = true,
  gridSize = 20,
  snapToGrid = true,

  screenToWorld,
  zoomAtPoint,

  tool,
  setTool,

  selectEntity,
  clearSelection,
  createRectangle,
  createCircle,
  createLine,
  createPolyline,
  moveEntity,
  panViewport,

  activeMode = "SITE",
  warningMessage = null,

  style = {},
}) => {
  const svgRef = useRef(null);

  const [viewportSize, setViewportSize] = useState({
    width: 1000,
    height: typeof height === "number" ? height : 600,
  });

  const [draggingId, setDraggingId] = useState(null);
  const [dragStart, setDragStart] = useState(null);

  const [drawStart, setDrawStart] = useState(null);
  const [lineStart, setLineStart] = useState(null);
  const [polyPoints, setPolyPoints] = useState([]);

  const [hoverPoint, setHoverPoint] = useState(null);
  const [hoverScreenPoint, setHoverScreenPoint] = useState(null);

  const [isPanning, setIsPanning] = useState(false);

  const viewportWidth = viewportSize.width;
  const viewportHeight = viewportSize.height;

  const activePalette = useMemo(() => {
    return getActivePalette(activeMode);
  }, [activeMode]);

  useEffect(() => {
    if (!svgRef.current) return;

    const updateSize = () => {
      const rect = svgRef.current.getBoundingClientRect();

      setViewportSize({
        width: rect.width || 1000,
        height: rect.height || (typeof height === "number" ? height : 600),
      });
    };

    updateSize();

    const observer = new ResizeObserver(() => {
      updateSize();
    });

    observer.observe(svgRef.current);
    window.addEventListener("resize", updateSize);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateSize);
    };
  }, [height]);

  const grid = useMemo(
    () =>
      buildGridLines({
        width: viewportWidth,
        height: viewportHeight,
        gridSize,
        offsetX,
        offsetY,
        zoom,
      }),
    [viewportWidth, viewportHeight, gridSize, offsetX, offsetY, zoom]
  );

  const clearDraftState = () => {
    setDrawStart(null);
    setLineStart(null);
    setPolyPoints([]);
  };

  const getPointerData = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;

    const rawWorld = screenToWorld(sx, sy);
    const world = snapPoint(rawWorld, gridSize, snapToGrid);

    return {
      sx,
      sy,
      rawWorld,
      world,
    };
  };

  const getDraftReferencePoint = () => {
    if (tool === drawSpaceConfig.tools.AREA_RECT && drawStart) return drawStart;
    if (tool === drawSpaceConfig.tools.CIRCLE && drawStart) return drawStart;
    if (tool === drawSpaceConfig.tools.LINE && lineStart) return lineStart;

    if (
      tool === drawSpaceConfig.tools.AREA_POLYLINE &&
      Array.isArray(polyPoints) &&
      polyPoints.length > 0
    ) {
      return polyPoints[polyPoints.length - 1];
    }

    return null;
  };

  const finalizePolyline = () => {
    if (polyPoints.length >= 3) {
      createPolyline(polyPoints);
    }
    setPolyPoints([]);
  };

  const finalizeLineWithHover = () => {
    if (!lineStart || !hoverPoint) return;

    createLine({
      x1: lineStart.x,
      y1: lineStart.y,
      x2: hoverPoint.x,
      y2: hoverPoint.y,
    });

    setLineStart(null);
  };

  const handleMouseDown = (e) => {
    const { world, rawWorld } = getPointerData(e);

    if (e.button === 1) {
      e.preventDefault();
      setIsPanning(true);
      setDragStart(rawWorld);
      return;
    }

    if (e.button !== 0) return;

    if (
      tool === drawSpaceConfig.tools.AREA_RECT ||
      tool === drawSpaceConfig.tools.CIRCLE
    ) {
      clearSelection();
    }

    if (tool === drawSpaceConfig.tools.AREA_RECT) {
      setDrawStart(world);
      return;
    }

    if (tool === drawSpaceConfig.tools.CIRCLE) {
      setDrawStart(world);
      return;
    }

    if (tool === drawSpaceConfig.tools.PAN) {
      setIsPanning(true);
      setDragStart(rawWorld);
      return;
    }

    if (tool === drawSpaceConfig.tools.SELECT) {
      clearSelection();
      clearDraftState();
    }
  };

  const handleMouseUp = (e) => {
    if (e.button !== 0 && e.button !== 1) return;

    const { world } = getPointerData(e);

    if (tool === drawSpaceConfig.tools.AREA_RECT && drawStart) {
      const width = world.x - drawStart.x;
      const height = world.y - drawStart.y;

      if (Math.abs(width) > 0 || Math.abs(height) > 0) {
        createRectangle({
          x: drawStart.x,
          y: drawStart.y,
          width,
          height,
        });
      }

      setDrawStart(null);
      return;
    }

    if (tool === drawSpaceConfig.tools.CIRCLE && drawStart) {
      const dx = world.x - drawStart.x;
      const dy = world.y - drawStart.y;
      const r = Math.sqrt(dx * dx + dy * dy);

      if (r > 0) {
        createCircle({
          cx: drawStart.x,
          cy: drawStart.y,
          r,
        });
      }

      setDrawStart(null);
      return;
    }

    if (isPanning) {
      setIsPanning(false);
      setDragStart(null);
    }
  };

  const handleMouseMove = (e) => {
    const { sx, sy, world, rawWorld } = getPointerData(e);

    setHoverPoint(world);
    setHoverScreenPoint({ x: sx, y: sy });

    if (isPanning && dragStart) {
      const dx = rawWorld.x - dragStart.x;
      const dy = rawWorld.y - dragStart.y;

      panViewport(dx * zoom, dy * zoom);
      setDragStart(rawWorld);
      return;
    }

    if (draggingId && dragStart) {
      const dx = world.x - dragStart.x;
      const dy = world.y - dragStart.y;
      moveEntity(draggingId, dx, dy);
      setDragStart(world);
    }
  };

  const handleMouseLeave = () => {
    setDraggingId(null);
    setDragStart(null);
    setHoverPoint(null);
    setHoverScreenPoint(null);
    setIsPanning(false);
  };

  const handleWheel = (e) => {
    if (!zoomAtPoint) return;

    e.preventDefault();

    const rect = svgRef.current.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;

    zoomAtPoint(sx, sy, e.deltaY);
  };

  useEffect(() => {
    const handleWindowMouseUp = () => {
      setDraggingId(null);
      setDragStart(null);
      setIsPanning(false);
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        clearDraftState();
        setHoverPoint(null);
        setHoverScreenPoint(null);

        if (setTool) {
          setTool(drawSpaceConfig.tools.SELECT);
        }
      }

      if (e.key === "Enter") {
        if (tool === drawSpaceConfig.tools.AREA_POLYLINE) {
          finalizePolyline();
        }

        if (tool === drawSpaceConfig.tools.LINE) {
          finalizeLineWithHover();
        }
      }
    };

    window.addEventListener("mouseup", handleWindowMouseUp);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("mouseup", handleWindowMouseUp);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [tool, polyPoints, lineStart, hoverPoint, setTool]);

  useEffect(() => {
    if (tool === drawSpaceConfig.tools.SELECT) {
      clearDraftState();
    }
  }, [tool]);

  const startMove = (e, entity) => {
    if (entity.interactive === false) return;

    if (
      tool !== drawSpaceConfig.tools.SELECT &&
      tool !== drawSpaceConfig.tools.MOVE
    ) {
      return;
    }

    e.stopPropagation();

    const { world } = getPointerData(e);
    setDraggingId(entity.id);
    setDragStart(world);
    selectEntity(entity.id);
  };

  const handleCanvasClick = (e) => {
    if (e.button !== 0) return;

    const { world } = getPointerData(e);

    if (tool === drawSpaceConfig.tools.LINE) {
      clearSelection();

      if (!lineStart) {
        setLineStart(world);
      } else {
        createLine({
          x1: lineStart.x,
          y1: lineStart.y,
          x2: world.x,
          y2: world.y,
        });
        setLineStart(null);
      }
      return;
    }

    if (tool === drawSpaceConfig.tools.AREA_POLYLINE) {
      clearSelection();
      setPolyPoints((prev) => [...prev, world]);
    }
  };

  const handleContextMenu = (e) => {
    if (tool === drawSpaceConfig.tools.AREA_POLYLINE) {
      e.preventDefault();
      finalizePolyline();
    }

    if (tool === drawSpaceConfig.tools.LINE && lineStart) {
      e.preventDefault();
      finalizeLineWithHover();
    }
  };

  const renderRect = (entity) => {
    const g = entity.geometry;
    const selected = entity.interactive !== false && entity.id === selectedId;
    const palette = getPaletteByKey(entity.paletteKey, activePalette);

    const x = Math.min(g.x, g.x + g.width);
    const y = Math.min(g.y, g.y + g.height);
    const width = Math.abs(g.width);
    const height = Math.abs(g.height);

    return (
      <g key={entity.id}>
        <rect
          x={x * zoom + offsetX}
          y={y * zoom + offsetY}
          width={width * zoom}
          height={height * zoom}
          fill={palette.fill}
          stroke={selected ? palette.selectedStroke : palette.stroke}
          strokeWidth={selected ? 2 : 1}
          onMouseDown={(e) => startMove(e, entity)}
          onClick={(e) => {
            if (entity.interactive === false) return;
            e.stopPropagation();
            selectEntity(entity.id);
          }}
        />
      </g>
    );
  };

  const renderCircle = (entity) => {
    const g = entity.geometry;
    const selected = entity.interactive !== false && entity.id === selectedId;
    const palette = getPaletteByKey(entity.paletteKey, activePalette);

    return (
      <circle
        key={entity.id}
        cx={g.cx * zoom + offsetX}
        cy={g.cy * zoom + offsetY}
        r={Math.abs(g.r) * zoom}
        fill={palette.fill}
        stroke={selected ? palette.selectedStroke : palette.stroke}
        strokeWidth={selected ? 2 : 1}
        onMouseDown={(e) => startMove(e, entity)}
        onClick={(e) => {
          if (entity.interactive === false) return;
          e.stopPropagation();
          selectEntity(entity.id);
        }}
      />
    );
  };

  const renderLine = (entity) => {
    const g = entity.geometry;
    const selected = entity.interactive !== false && entity.id === selectedId;
    const palette = getPaletteByKey(entity.paletteKey, activePalette);

    return (
      <g key={entity.id}>
        <line
          x1={g.x1 * zoom + offsetX}
          y1={g.y1 * zoom + offsetY}
          x2={g.x2 * zoom + offsetX}
          y2={g.y2 * zoom + offsetY}
          stroke={selected ? palette.selectedStroke : palette.stroke}
          strokeWidth={selected ? 2.5 : 1.5}
        />
        {entity.interactive !== false && (
          <line
            x1={g.x1 * zoom + offsetX}
            y1={g.y1 * zoom + offsetY}
            x2={g.x2 * zoom + offsetX}
            y2={g.y2 * zoom + offsetY}
            stroke="transparent"
            strokeWidth={10}
            onMouseDown={(e) => startMove(e, entity)}
            onClick={(e) => {
              e.stopPropagation();
              selectEntity(entity.id);
            }}
          />
        )}
      </g>
    );
  };

  const renderPolyline = (entity) => {
    const points = entity.geometry?.points ?? [];
    const selected = entity.interactive !== false && entity.id === selectedId;
    const palette = getPaletteByKey(entity.paletteKey, activePalette);

    const screenPoints = points
      .map((p) => `${p.x * zoom + offsetX},${p.y * zoom + offsetY}`)
      .join(" ");

    return (
      <g key={entity.id}>
        <polygon
          points={screenPoints}
          fill={palette.fill}
          stroke={selected ? palette.selectedStroke : palette.stroke}
          strokeWidth={selected ? 2 : 1.5}
          onMouseDown={(e) => startMove(e, entity)}
          onClick={(e) => {
            if (entity.interactive === false) return;
            e.stopPropagation();
            selectEntity(entity.id);
          }}
        />
      </g>
    );
  };

  const renderEntity = (entity) => {
    if (!entity?.geometry) return null;

    if (entity.type === drawSpaceConfig.entityTypes.AREA_RECT) {
      return renderRect(entity);
    }

    if (entity.type === drawSpaceConfig.entityTypes.CIRCLE) {
      return renderCircle(entity);
    }

    if (entity.type === drawSpaceConfig.entityTypes.LINE) {
      return renderLine(entity);
    }

    if (entity.type === drawSpaceConfig.entityTypes.AREA_POLYLINE) {
      return renderPolyline(entity);
    }

    return null;
  };

  const renderDraft = () => {
    if (!hoverPoint) return null;

    if (tool === drawSpaceConfig.tools.AREA_RECT && drawStart) {
      const x = Math.min(drawStart.x, hoverPoint.x);
      const y = Math.min(drawStart.y, hoverPoint.y);
      const width = Math.abs(hoverPoint.x - drawStart.x);
      const height = Math.abs(hoverPoint.y - drawStart.y);

      return (
        <rect
          x={x * zoom + offsetX}
          y={y * zoom + offsetY}
          width={width * zoom}
          height={height * zoom}
          fill={activePalette.previewFill}
          stroke={activePalette.previewStroke}
          strokeWidth={1.5}
          strokeDasharray="6 4"
        />
      );
    }

    if (tool === drawSpaceConfig.tools.CIRCLE && drawStart) {
      const dx = hoverPoint.x - drawStart.x;
      const dy = hoverPoint.y - drawStart.y;
      const r = Math.sqrt(dx * dx + dy * dy);

      return (
        <circle
          cx={drawStart.x * zoom + offsetX}
          cy={drawStart.y * zoom + offsetY}
          r={Math.abs(r) * zoom}
          fill={activePalette.previewFill}
          stroke={activePalette.previewStroke}
          strokeWidth={1.5}
          strokeDasharray="6 4"
        />
      );
    }

    if (tool === drawSpaceConfig.tools.LINE && lineStart) {
      return (
        <line
          x1={lineStart.x * zoom + offsetX}
          y1={lineStart.y * zoom + offsetY}
          x2={hoverPoint.x * zoom + offsetX}
          y2={hoverPoint.y * zoom + offsetY}
          stroke={activePalette.previewStroke}
          strokeWidth={1.5}
          strokeDasharray="6 4"
        />
      );
    }

    if (tool === drawSpaceConfig.tools.AREA_POLYLINE && polyPoints.length > 0) {
      const screenPoints = [...polyPoints, hoverPoint]
        .map((p) => `${p.x * zoom + offsetX},${p.y * zoom + offsetY}`)
        .join(" ");

      return (
        <polyline
          points={screenPoints}
          fill="none"
          stroke={activePalette.previewStroke}
          strokeWidth={1.5}
          strokeDasharray="6 4"
        />
      );
    }

    return null;
  };

  const draftReferencePoint = getDraftReferencePoint();
  const showCreationMarker = isCreationTool(tool) && !!hoverPoint;
  const showFloatingInfo =
    isCreationTool(tool) && !!hoverPoint && !!hoverScreenPoint;

  const floatingText = draftReferencePoint
    ? `ΔX: ${(hoverPoint.x - draftReferencePoint.x).toFixed(1)} | ΔY: ${(hoverPoint.y - draftReferencePoint.y).toFixed(1)}`
    : `X: ${hoverPoint?.x?.toFixed(1) ?? "-"} | Y: ${hoverPoint?.y?.toFixed(1) ?? "-"}`;

  const statusModeLabel = getModeLabel(activeMode);

  const statusCoordinates = hoverPoint
    ? `X: ${hoverPoint.x.toFixed(1)} | Y: ${hoverPoint.y.toFixed(1)}`
    : "X: - | Y: -";

  return (
    <div
      style={{
        width,
        height,
        background: theme.colors.background.panel,
        position: "relative",
        ...style,
      }}
    >
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleCanvasClick}
        onContextMenu={handleContextMenu}
        onWheel={handleWheel}
        style={{
          cursor: getCursor(tool, isPanning),
          display: "block",
        }}
      >
        {showGrid &&
          grid.minor.map((line, index) => (
            <line
              key={`minor-${index}`}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              stroke={theme.colors.borders.panel}
              strokeWidth={0.5}
            />
          ))}

        {showGrid &&
          grid.major.map((line, index) => (
            <line
              key={`major-${index}`}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              stroke={theme.colors.borders.default}
              strokeWidth={1}
            />
          ))}

        {entities.map(renderEntity)}
        {renderDraft()}

        {showCreationMarker && (
          <circle
            cx={hoverPoint.x * zoom + offsetX}
            cy={hoverPoint.y * zoom + offsetY}
            r={3}
            fill={activePalette.previewStroke}
            pointerEvents="none"
          />
        )}
      </svg>

      {showFloatingInfo && (
        <div
          style={{
            position: "absolute",
            left: hoverScreenPoint.x + 14,
            top: hoverScreenPoint.y + 14,
            padding: "2px 6px",
            fontFamily: theme.fonts.body,
            fontSize: theme.fontSizes.xs,
            color: theme.colors.text.body,
            background: "rgba(255,255,255,0.96)",
            border: `1px solid ${theme.colors.borders.default}`,
            borderRadius: theme.borders.radius.sm,
            boxShadow: theme.shadows.xs,
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          {floatingText}
        </div>
      )}

      <div
        style={{
          position: "absolute",
          left: 10,
          right: 10,
          bottom: 10,
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          pointerEvents: "none",
          gap: 10,
        }}
      >
        <div
          style={{
            justifySelf: "start",
            padding: "4px 8px",
            fontFamily: theme.fonts.body,
            fontSize: theme.fontSizes.xs,
            color: theme.colors.text.body,
            background: "rgba(255,255,255,0.92)",
            border: `1px solid ${theme.colors.borders.default}`,
            borderRadius: theme.borders.radius.md,
            boxShadow: theme.shadows.xs,
            whiteSpace: "nowrap",
          }}
        >
          {statusModeLabel}
        </div>

        <div
          style={{
            justifySelf: "center",
            minHeight: 24,
            padding: warningMessage ? "4px 10px" : "0",
            fontFamily: theme.fonts.body,
            fontSize: theme.fontSizes.xs,
            color: warningMessage ? "#6e4b00" : "transparent",
            background: warningMessage
              ? "rgba(255, 243, 205, 0.96)"
              : "transparent",
            border: warningMessage
              ? "1px solid rgba(214, 174, 0, 0.45)"
              : "none",
            borderRadius: theme.borders.radius.md,
            boxShadow: warningMessage ? theme.shadows.xs : "none",
            whiteSpace: "nowrap",
            textAlign: "center",
          }}
        >
          {warningMessage ? `⚠ ${warningMessage}` : ""}
        </div>

        <div
          style={{
            justifySelf: "end",
            padding: "4px 8px",
            fontFamily: theme.fonts.body,
            fontSize: theme.fontSizes.xs,
            color: theme.colors.text.body,
            background: "rgba(255,255,255,0.92)",
            border: `1px solid ${theme.colors.borders.default}`,
            borderRadius: theme.borders.radius.md,
            boxShadow: theme.shadows.xs,
            whiteSpace: "nowrap",
          }}
        >
          {statusCoordinates}
        </div>
      </div>
    </div>
  );
};

export default DrawViewport;